import { syncReports as defaultSyncReports } from './ReportSync'

// Page-driven fallback outbox flusher for browsers without Background Sync
// (Safari/Firefox: no SyncManager, so no closed-tab/background retry exists).
// It drains the same IndexedDB outbox the service-worker path uses, via the
// shared syncReports() state machine, while the app is open: on start, on the
// `online` event, and on a modest interval while online. Concurrent flushes are
// coalesced so at most one drain runs at a time.
class OutboxFlusher {
  static DEFAULT_INTERVAL_MS = 30000

  // options:
  //   deps      { reportService, timeouts } — passed straight to syncReports
  //   store     { openDatabase, getReports } — ReportStore helpers
  //   syncReports (optional) — overridable for tests
  //   isOnline    (optional) — overridable for tests; defaults to navigator.onLine
  //   intervalMs  (optional) — flush interval while online
  constructor (options = {}) {
    const { deps, store, syncReports, isOnline, intervalMs } = options
    this.deps = deps
    this.openDatabase = store.openDatabase
    this.getReports = store.getReports
    this.syncReports = syncReports || defaultSyncReports
    this._isOnlineFn = isOnline || null
    this.intervalMs = intervalMs || OutboxFlusher.DEFAULT_INTERVAL_MS

    this._inFlight = false
    this._pendingRequest = false
    this._timer = null
    this._started = false
    this._onlineHandler = () => this.requestFlush()
  }

  // Begins the online + interval triggers and performs an initial drain. Safe to
  // call more than once (subsequent calls are no-ops).
  start () {
    if (this._started) { return }
    this._started = true

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('online', this._onlineHandler)
    }
    if (typeof setInterval !== 'undefined') {
      this._timer = setInterval(() => {
        if (this._isOnline()) { this.requestFlush() }
      }, this.intervalMs)
    }

    this.requestFlush()
  }

  // Stops the triggers and clears the interval/listener.
  stop () {
    this._started = false
    if (this._timer && typeof clearInterval !== 'undefined') {
      clearInterval(this._timer)
    }
    this._timer = null
    if (typeof window !== 'undefined' && window.removeEventListener) {
      window.removeEventListener('online', this._onlineHandler)
    }
  }

  // Fire-and-forget flush trigger. Never rejects to the caller; a still-pending
  // outbox (syncReports throwing) is retried on the next trigger.
  requestFlush () {
    this.flush().catch(() => {})
  }

  // alias
  flushNow () {
    return this.requestFlush()
  }

  // Drains the outbox, coalescing a flush requested while one is already running
  // so at most one drain runs at a time and a mid-flush enqueue is not missed.
  async flush () {
    if (this._inFlight) {
      this._pendingRequest = true
      return
    }
    this._inFlight = true
    try {
      do {
        this._pendingRequest = false
        await this._drainOnce()
      } while (this._pendingRequest && this._isOnline())
    } finally {
      this._inFlight = false
    }
  }

  // Single drain pass. Offline-, empty- and error-safe: swallows the
  // /retry sync/ throw (still-pending is normal) and any IndexedDB error (e.g.
  // no fake IDB in tests) so it never crashes the page.
  async _drainOnce () {
    if (!this._isOnline()) { return }
    try {
      const db = await this.openDatabase()
      const records = await this.getReports(db)
      if (!records || records.length === 0) { return }
      await this.syncReports(this.deps, db, records)
    } catch (err) {
      // still-pending / transient / unavailable IDB -> retried on next trigger
    }
  }

  _isOnline () {
    if (this._isOnlineFn) { return this._isOnlineFn() }
    return typeof navigator !== 'undefined' ? navigator.onLine : false
  }
}

export default OutboxFlusher
